import React, { useEffect, useState, useRef, useMemo, useCallback } from "react";
import { useIntl, useLocation } from 'umi';
import { Segment, Button } from "tea-component";
import PipelineCalendar from '@sg/pages/StudioManagement/PortfolioOverview/components/PipelineCalendar';
// Components
import MetricCard from '@/pages/strategic/pages/BusinessManagement/MetricCard';
import CardCommon from '@sg/components/CardCommon';
import GameSelect from '@sg/components/GameSelect';
import AccessRestricted from '@sg/components/AccessRestricted';

// Utils and Config
import { COEFFICIENT, STUDIO_STUDIOS_TOTAL_REVENUE_CARD, STUDIO_STUDIOS_TOTAL_PROFIT_CARD } from '../config';
import { EDIT_PAGE_KEY_MAP } from '@sg/hooks/useAutoSaveParams';

// Local imports
import {
  getOptionsSegment,
} from './config';
import {
  useStudioList,
  useStudioOverview,
  useStudioTrend,
} from './hooks/useStudioData';
import { useNotesAutoScroll } from './hooks/useNotesAutoScroll';
import usePipelineCalendarAutoScroll from '@sg/pages/StudioManagement/PortfolioOverview/hooks/usePipelineCalendarAutoScroll';
import { STRATEGIC_STORAGE_KEY } from '@sg/constants/storageKeys';
import { useStudiosBatchNotes } from './hooks/useStudiosBatchNotes';
import { useStudiosBatchNotesAutoSave } from './hooks/useStudiosBatchNotesAutoSave';
import EditNotesModal from '@sg/pages/StudioManagement/InDevProjects/pages/InDevProjectsAll/component/EditNotesModal';
import AutoSaveModal from '@sg/components/AutoSaveModal';
import RoutePrompt from '@sg/components/RoutePrompt';
import { useStudioEyesViewState, STUDIOS_EYES_VIEW_KEYS } from '../hooks/useStudioEyesViewState';
import moment from 'moment';

import './index.scss';
import { getButtonPermission } from "@sg/_config/permission";
import { usePermissionReady } from '@sg/hooks/usePermissionReady';
import { createBtnPoint, SGBuriedBtnPoint, StudiosTab } from '@/pages/strategic/utils/buriedPoint';
import PlainText from "@sg/components/PlainText";
import CommonBubble from '@home/components/CommonBubble';
import SvgIcon from '@/pages/home/components/SvgIcon';
import FinancialsCard from './component/FinancialsCard';
import HeadcountCard from '../component/HeadcountCard';
import MetricsDownloadModal, {
  STUDIOS_METRICS_DOWNLOAD_CONFIG,
} from '../component/MetricsDownloadModal';
import RevenueProfitTrendCard from './component/RevenueProfitTrendCard';
const Studios: React.FC<any> = (props) => {
  const { selectedMonth, onEditingChange, onStudioChange, maxDataTime } = props; // selectedMonth is already formatted as "YYYY-MM" string or null
  const { formatMessage } = useIntl();
  const { ready: permissionReady } = usePermissionReady();
  const location = useLocation();


  // Refs
  const notesRef = useRef<HTMLDivElement>(null);
  const pipelineCalendarRef = useRef<HTMLDivElement>(null);
  const plainTextRef = useRef<any>(null);
  const topRef = useRef<HTMLDivElement>(null);

  // Extract studio_id from URL for initial selection
  const urlStudioId = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get('studio_id') || undefined;
  }, [location.search]);

  const buttonPermission = useMemo(() => ({
    Appendix: getButtonPermission('studio_studios_Appendix&Notes'),
    pipelineCalendar: getButtonPermission('studio_studios_pipelineCalendar'),
  }), [permissionReady]);

  // Scroll to top on mount
  useEffect(() => {
    setTimeout(() => {
      topRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, 400);
  }, []);

  // Custom hooks
  const {
    gameInfo,
    hasStudioAccess,
    fetchStudioData,
    handleStudioChange,
    handleStudioDataLoaded,
  } = useStudioList();

  const { studioOverviewData, studioOverviewDataTime } = useStudioOverview(gameInfo.unified_id, selectedMonth);

  const [activeCard, setActiveCard] = useState<any>('Revenue');
  const {
    revenueData,
    currentMetricsData,
    trendLoading,
  } = useStudioTrend(
    gameInfo.unified_id,
    activeCard,
    selectedMonth
  );

  // Notes editing state
  const [isNotesEditing, setIsNotesEditing] = useState(false);
  const [isEditAllNotesVisible, setIsEditAllNotesVisible] = useState(false);
  const batchNotesSnapshotRef = useRef<any[] | null>(null);

  // Notify parent when studio changes
  useEffect(() => {
    if (onStudioChange && gameInfo.unified_id) {
      onStudioChange(gameInfo.unified_id);
    }
  }, [gameInfo.unified_id, onStudioChange]);

  // Notes auto-scroll hook
  useNotesAutoScroll(notesRef);
  usePipelineCalendarAutoScroll(
    pipelineCalendarRef,
    STRATEGIC_STORAGE_KEY.STUDIOS_PIPELINE_CALENDAR_SCROLL,
  );

  // State
  const [activeDateTypeSegment, setActiveDateTypeSegment] = useState('month');
  const [metricsDownloadVisible, setMetricsDownloadVisible] = useState(false);
  const { smallChartViewState, updateStudioEyesViewState } = useStudioEyesViewState({
    viewKeys: STUDIOS_EYES_VIEW_KEYS,
  });

  // CoQnvert selectedMonth string to moment object for PlainText component
  // Use studioOverviewDataTime from API if available, otherwise fall back to selectedMonth
  const selectedMonthMoment = useMemo(() => {
    const dateStr = studioOverviewDataTime || selectedMonth;
    return dateStr ? moment(dateStr, 'YYYY-MM') : null;
  }, [studioOverviewDataTime, selectedMonth]);

  // Compute params for PlainText Notes component
  // If studioOverviewDataTime exists, don't include logical_flag (use date from selectedMonthMoment)
  // If studioOverviewDataTime doesn't exist, include logical_flag to get latest data
  const notesParams = useMemo(() => {
    const baseParams: any = {
      studio_id: gameInfo.unified_id,
      source: "studios",
    };

    // The logic here is different from other page as requirement stated only "PortfolioOverview" and "Studios" have to follow the updateTime provided by studioOverviewDataTime
    // So logical flag is optional here only if we don't have studioOverviewDataTime
    if (!studioOverviewDataTime) {
      baseParams.logical_flag = "has_date_latest";
    }

    return baseParams;
  }, [gameInfo.unified_id, studioOverviewDataTime]);

  const notesBatchDate = useMemo(() => {
    if (selectedMonthMoment) {
      return selectedMonthMoment.format('YYYY-MM-DD');
    }
    if (studioOverviewDataTime) {
      return moment(studioOverviewDataTime, 'YYYY-MM').format('YYYY-MM-DD');
    }
    if (selectedMonth) {
      return moment(selectedMonth, 'YYYY-MM').format('YYYY-MM-DD');
    }
    return undefined;
  }, [selectedMonthMoment, studioOverviewDataTime, selectedMonth]);

  const {
    notes: batchNotes,
    setNotes: setBatchNotes,
    noteMonth: batchNoteMonth,
    fetching: batchNotesFetching,
    saveStudiosNotes,
    fetchStudiosNotes,
  } = useStudiosBatchNotes(notesBatchDate, { enabled: buttonPermission.Appendix });

  const {
    autoSaveVisible,
    hasUnsavedChanges: hasBatchNotesUnsavedChanges,
    captureBaseline: captureBatchNotesBaseline,
    handleEditWithAutoSave: handleBatchNotesEditWithAutoSave,
    handleAutoSaveConfirm: handleBatchNotesAutoSaveConfirm,
    handleAutoSaveCancel: handleBatchNotesAutoSaveCancel,
    clearAutoSave: clearBatchNotesAutoSave,
  } = useStudiosBatchNotesAutoSave({
    isEditing: isEditAllNotesVisible,
    monthKey: batchNoteMonth,
    notes: batchNotes,
    setNotes: setBatchNotes,
  });

  // Memoized studio information for buried point extInfo
  const studioInfo = useMemo(() => {
    const studioName = gameInfo?.name || 'Unknown';
    const studioId = gameInfo?.unified_id || '';
    return `Studio: ${studioName}${studioId ? ` (ID: ${studioId})` : ''}`;
  }, [gameInfo?.name, gameInfo?.unified_id]);

  // Memoized configs
  const optionsSegment = useMemo(() => getOptionsSegment(formatMessage), [formatMessage]);

  const revenueCardConfig = useMemo(() => STUDIO_STUDIOS_TOTAL_REVENUE_CARD(studioOverviewData), [studioOverviewData]);

  const profitCardConfig = useMemo(() => STUDIO_STUDIOS_TOTAL_PROFIT_CARD(studioOverviewData), [studioOverviewData]);

  // Notes editing change handler
  const handleNotesEditingChange = (isEditing: boolean) => {
    setIsNotesEditing(isEditing);

    // Notify parent component about editing state change to disable the date selection
    if (onEditingChange) {
      onEditingChange(isEditing || isEditAllNotesVisible);
    }
  };

  const openEditAllNotesModal = useCallback(() => {
    batchNotesSnapshotRef.current = JSON.parse(JSON.stringify(batchNotes || []));
    captureBatchNotesBaseline(batchNotes || []);
    setIsEditAllNotesVisible(true);
    if (onEditingChange) {
      onEditingChange(isNotesEditing || true);
    }
  }, [batchNotes, captureBatchNotesBaseline, onEditingChange, isNotesEditing]);

  const handleEditAllNotesOpen = () => {
    const btnPoint = createBtnPoint(
      StudiosTab,
      'editAllStudiosNotes',
      `Edit All Studios Notes - ${studioInfo}`,
    );
    if (btnPoint) {
      SGBuriedBtnPoint(btnPoint);
    }
    handleBatchNotesEditWithAutoSave(openEditAllNotesModal);
  };

  const handleEditAllNotesClose = () => {
    if (batchNotesSnapshotRef.current) {
      setBatchNotes(batchNotesSnapshotRef.current);
    }
    batchNotesSnapshotRef.current = null;
    clearBatchNotesAutoSave();
    setIsEditAllNotesVisible(false);
    if (onEditingChange) {
      onEditingChange(isNotesEditing);
    }
  };

  const handleEditAllNotesConfirm = async () => {
    const isSuccess = await saveStudiosNotes();
    if (!isSuccess) {
      return;
    }
    batchNotesSnapshotRef.current = null;
    clearBatchNotesAutoSave();
    setIsEditAllNotesVisible(false);
    plainTextRef.current?.refresh?.();
    if (onEditingChange) {
      onEditingChange(isNotesEditing);
    }
  };

  // Show Access Restricted if no studios available after permission filtering
  if (!hasStudioAccess) {
    return (
      <AccessRestricted />
    );
  }

  return (
    <div className="dbs-home-studio-card-studio">
      {/* <div ref={topRef} style={{ position: 'relative', top: '-160px' }}></div> */}
      <GameSelect
        fetchData={fetchStudioData}
        onChange={handleStudioChange}
        onDataLoaded={handleStudioDataLoaded}
        defaultUnifiedId={urlStudioId}
        urlParamKey="studio_id"
        urlUpdateCondition={(loc) => {
          const params = new URLSearchParams(loc.search);
          return params.get('subtitle') === 'studio';
        }}
        clearUrlParamsOnChange={['meeting_id']}
      />

      {/* Revenue & Profit Cards */}
      <div className='dbs-home-studio-card'>
        <MetricCard
          key={revenueCardConfig?.title}
          coefficient={COEFFICIENT}
          config={{
            ...revenueCardConfig,
            titleVariable: gameInfo.name,
          }}
          capsuleMode={'single'}
          capsuleHeight={40}
          cardKey={0}
          isActive={activeCard === 'Revenue' ? 1 : 2}
          onClick={() => {
            if (activeCard !== 'Revenue') {
              // Track buried point for Revenue metric card click
              const btnPoint = createBtnPoint(
                StudiosTab,
                'revenueMetricCard',
                `Revenue Metric Card - ${studioInfo}`
              );
              if (btnPoint) {
                SGBuriedBtnPoint(btnPoint);
              }
              setActiveCard('Revenue');
            }
          }}
        />
        <MetricCard
          key={profitCardConfig?.title}
          coefficient={COEFFICIENT}
          config={{
            ...profitCardConfig,
            titleVariable: gameInfo.name,
          }}
          capsuleMode={'single'}
          capsuleHeight={40}
          cardKey={3}
          isActive={activeCard === 'Profit' ? 1 : 2}
          onClick={() => {
            if (activeCard !== 'Profit') {
              // Track buried point for Profit metric card click
              const btnPoint = createBtnPoint(
                StudiosTab,
                'profitMetricCard',
                `Profit Metric Card - ${studioInfo}`
              );
              if (btnPoint) {
                SGBuriedBtnPoint(btnPoint);
              }
              setActiveCard('Profit');
            }
          }}
        />
      </div>

      {/* Revenue/Profit Trend Chart */}
      <RevenueProfitTrendCard
        activeCard={activeCard}
        revenueData={revenueData}
        currentMetricsData={currentMetricsData}
        trendLoading={trendLoading}
        studioId={gameInfo.unified_id}
        studioInfo={studioInfo}
      />

      {/* Notes Section with scroll anchor */}
      <div ref={notesRef} style={{ position: 'relative', top: '-50px', margin: '20px' }}></div>
      <PlainText
        ref={plainTextRef}
        key={`plaintext-${gameInfo.unified_id}`}
        title={formatMessage({ id: 'dbs_notes' })}
        url="/api/v1/mgmt_pc/note/get"
        editUrl="/api/v1/mgmt_pc/note/update"
        params={notesParams}
        showDatePicker={false}
        selectedMonth={selectedMonthMoment}
        maxDataTime={maxDataTime}
        showTimeInTitle={true}
        editable={true}
        userPermissions={{ canEdit: buttonPermission.Appendix }}
        tipString={'Data is updated by the Studio Operations Management team.'}
        autoSavePageKey={EDIT_PAGE_KEY_MAP.studio_studios_notes}
        autoSaveId={gameInfo.unified_id}
        // File Upload
        allowFileUpload={true}
        uploadDomain="mgmt/studio/studios/notes"
        cosConfigDomain="mgmt/studio/studios/notes"
        onEditingChange={handleNotesEditingChange}
        onSave={() => {
          fetchStudiosNotes();
        }}
        extraPreviewActions={
          buttonPermission.Appendix && !isNotesEditing ? (
            <span className="dbs-studios-notes-batch-edit-actions">
              <span className="dbs-studios-notes-batch-edit-actions__separator" aria-hidden="true" />
              <Button
                type="text"
                className="dbs-studios-notes-batch-edit-btn"
                onClick={(e) => {
                  e?.stopPropagation();
                  handleEditAllNotesOpen();
                }}
                disabled={batchNotesFetching && !batchNotes.length}
              >
                <span className="dbs-studios-notes-batch-edit-btn__icon" aria-hidden="true" />
                {formatMessage({ id: 'dbs_edit_all_studios_notes' })}
              </Button>
            </span>
          ) : null
        }
        onEditClick={(type) => {
          // Track buried point for Notes Edit button or Add Content link click
          const extInfo = type === 'Add Content Link'
            ? `Notes Edit - Add Content Link - ${studioInfo}`
            : `Notes Edit - Edit Button - ${studioInfo}`;
          const btnPoint = createBtnPoint(
            StudiosTab,
            'notesEdit',
            extInfo
          );
          if (btnPoint) {
            SGBuriedBtnPoint(btnPoint);
          }
        }}
      />

      {/* Financials & Headcount Section */}
      <CardCommon
        title={
          <Segment
            className="dbs-gradient-segment"
            options={optionsSegment}
            value={activeDateTypeSegment}
            onChange={(value) => {
              // Track buried point for segment change
              if (value === 'month') {
                const btnPoint = createBtnPoint(
                  StudiosTab,
                  'byMonth',
                  `By Month - ${studioInfo}`
                );
                if (btnPoint) {
                  SGBuriedBtnPoint(btnPoint);
                }
              } else if (value === 'year') {
                const btnPoint = createBtnPoint(
                  StudiosTab,
                  'byYear',
                  `By Year - ${studioInfo}`
                );
                if (btnPoint) {
                  SGBuriedBtnPoint(btnPoint);
                }
              }
              setActiveDateTypeSegment(value);
            }}
          />
        }
        operation={
          // Same SvgIcon + card-slot classes as Chart/Operations download toolbar icon.
          <div className="dbs-card-slot-item">
            <CommonBubble content={formatMessage({ id: 'dbs_download' })} dark>
              <i
                className="card-slot-item dbo-show"
                onClick={() => {
                  const btnPoint = createBtnPoint(
                    StudiosTab,
                    'metricsDownload',
                    `Studios Metrics - Download - ${studioInfo}`,
                  );
                  if (btnPoint) {
                    SGBuriedBtnPoint(btnPoint);
                  }
                  setMetricsDownloadVisible(true);
                }}
              >
                <SvgIcon type="download" className="card-slot-charicon" />
              </i>
            </CommonBubble>
          </div>
        }
      >
        <div className='dbs-home-studio-card dbs-home-by-date-studio-card'>
          {/* Studio Financials */}
          <FinancialsCard
            studioId={gameInfo.unified_id}
            activeDateTypeSegment={activeDateTypeSegment}
            selectedMonth={selectedMonth}
            smallChartViewState={smallChartViewState}
            updateStudioEyesViewState={updateStudioEyesViewState}
            studioInfo={studioInfo}
          />

          {/* Headcount / Staff Rate */}
          <HeadcountCard
            scope="studio"
            studioId={gameInfo.unified_id}
            studioName={gameInfo?.name ?? ''}
            activeDateTypeSegment={activeDateTypeSegment}
            selectedMonth={selectedMonth}
            smallChartViewState={smallChartViewState}
            updateStudioEyesViewState={updateStudioEyesViewState}
            studioInfo={studioInfo}
            buriedPointTab={StudiosTab}
          />
        </div>
      </CardCommon>
      <MetricsDownloadModal
        visible={metricsDownloadVisible}
        config={STUDIOS_METRICS_DOWNLOAD_CONFIG}
        studioId={gameInfo.unified_id}
        studioName={gameInfo?.name ?? ''}
        activeDateTypeSegment={activeDateTypeSegment}
        selectedMonth={selectedMonth}
        onClose={() => setMetricsDownloadVisible(false)}
      />
      <PipelineCalendar
        editable={buttonPermission.pipelineCalendar}
        page={'studios'}
        studioIdPa={gameInfo.unified_id}
        calendarRef={pipelineCalendarRef}
      />

      <EditNotesModal
        visible={isEditAllNotesVisible}
        onClose={handleEditAllNotesClose}
        onConfirm={handleEditAllNotesConfirm}
        month={batchNoteMonth}
        notes={batchNotes}
        setNotes={setBatchNotes}
        uploadDomain="mgmt/studio/studios/notes"
      />

      <AutoSaveModal
        visible={autoSaveVisible}
        onConfirm={() => {
          const restored = handleBatchNotesAutoSaveConfirm();
          if (!restored) {
            openEditAllNotesModal();
            return;
          }
          batchNotesSnapshotRef.current = JSON.parse(
            JSON.stringify(restored.baselineNotes),
          );
          setIsEditAllNotesVisible(true);
          if (onEditingChange) {
            onEditingChange(isNotesEditing || true);
          }
        }}
        onClose={() => {
          handleBatchNotesAutoSaveCancel();
          openEditAllNotesModal();
        }}
      />

      <RoutePrompt
        when={isEditAllNotesVisible && hasBatchNotesUnsavedChanges}
        onOk={async () => {
          await handleEditAllNotesConfirm();
        }}
      />
    </div>
  );
};

export default Studios;
